// src/pages/customer/Cart.jsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { getOrderCharges, DEFAULT_ORDER_CHARGES } from "../../services/firestoreService";
import { FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowRight, FiTruck, FiTag } from "react-icons/fi";
import toast from "react-hot-toast";

export default function Cart() {
  const { items, total, removeItem, updateQty, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Delivery-per-kg + SGST/CGST rates — set by the admin on the "Order Unit"
  // screen. Start with the known defaults so totals render immediately, then
  // swap in the live values once they've loaded from Firestore.
  const [charges, setCharges] = useState(DEFAULT_ORDER_CHARGES);

  useEffect(() => {
    getOrderCharges()
      .then(setCharges)
      .catch((err) => console.error("Failed to load order charges:", err));
  }, []);

  // "30 kg" / "5 pack(s)" depending on the product's unit
  const getQtyLabel = (item) => {
    if (item.unit === "pack") return item.packSize ? `${item.packSize} pack(s)` : null;
    return item.weightSize ? `${item.weightSize} kg` : null;
  };

  // "₹107/kg" / "₹99/pack" depending on the product's unit
  const getRateLabel = (item) => {
    if (item.unit === "pack") return item.pricePerPack ? `₹${item.pricePerPack}/pack` : null;
    return item.pricePerKg ? `₹${item.pricePerKg}/kg` : null;
  };

  // Total kg this cart item represents, used for the per-kg delivery charge.
  // "kg" unit items store their per-unit weight in weightSize (e.g. weightSize
  // "5" + qty 3 = 15 kg). "pack" unit items are counted, not weighed, so they
  // don't add to delivery weight.
  const getItemKg = (item) => {
    const qty = Number(item.qty) || 0;
    if (!qty) return 0;
    if (item.unit === "pack") return 0;
    const weightNum = parseFloat(item.weightSize);
    return weightNum ? weightNum * qty : 0;
  };

  const handleCheckout = () => {
    if (!user) { navigate("/login", { state: { from: { pathname: "/checkout" } } }); return; }
    navigate("/checkout");
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <Helmet>
          <title>Your Cart | Tripura Agro</title>
          <meta
            name="description"
            content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
          />
        </Helmet>
        <div className="text-center max-w-sm">
          <div className="w-24 h-24 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiShoppingBag className="w-10 h-10 text-primary-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-8 text-sm leading-relaxed">
            Looks like you haven't added anything yet. Start browsing fresh products!
          </p>
          <Link
            to="/products"
            className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold"
          >
            Browse Products <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // Same delivery / tax calculation as Checkout, so the number shown here
  // matches what the customer will actually pay.
  const totalKg       = items.reduce((sum, item) => sum + getItemKg(item), 0);
  const deliveryPerKg = Number(charges.deliveryPerKg) || 0;
  const sgstPercent   = Number(charges.sgstPercent) || 0;
  const cgstPercent   = Number(charges.cgstPercent) || 0;

  const deliveryCharge = totalKg * deliveryPerKg;
  const sgstAmount     = total * (sgstPercent / 100);
  const cgstAmount     = total * (cgstPercent / 100);
  const grandTotal     = total + deliveryCharge + sgstAmount + cgstAmount;

  const savings = items.reduce((acc, item) => acc + (item.originalPrice ? (item.originalPrice - item.price) * item.qty : 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

      <Helmet>
        <title>Your Cart | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shopping Cart</h1>
          <p className="text-sm text-gray-400 mt-0.5">{items.length} item{items.length !== 1 ? "s" : ""} in your bag</p>
        </div>
        <button
          onClick={() => { clearCart(); toast.success("Cart cleared"); }}
          className="text-xs font-medium text-red-400 hover:text-red-600 border border-red-100 hover:border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all"
        >
          Clear all
        </button>
      </div>

      {/* Delivery info banner */}
      {totalKg > 0 && deliveryPerKg > 0 && (
        <div className="mb-5 flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
          <FiTruck className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700 font-medium">
            Delivery charge is <span className="font-bold">₹{deliveryPerKg}/kg</span> — your cart weighs{" "}
            <span className="font-bold">{totalKg.toFixed(2)} kg</span>, so delivery comes to{" "}
            <span className="font-bold">₹{deliveryCharge.toFixed(2)}</span>.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Cart Items */}
        <div className="lg:col-span-7 space-y-3">
          {items.map((item) => {
            const itemKg = getItemKg(item);
            const itemDeliveryCharge = itemKg * deliveryPerKg;
            return (
              <div
                key={item.id}
                className="group bg-white border border-gray-100 rounded-2xl p-4 flex gap-4 hover:border-primary-200 hover:shadow-sm transition-all duration-200"
              >
                {/* Image */}
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl">🌱</div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        to={`/products/${item.id}`}
                        className="font-semibold text-gray-900 hover:text-primary-600 transition-colors text-sm leading-snug line-clamp-2 block"
                      >
                        {item.name}
                      </Link>
                      {(item.unit || getQtyLabel(item) || getRateLabel(item)) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {getQtyLabel(item) && (
                            <span className="inline-block text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-1.5 py-0.5">
                              📦 {getQtyLabel(item)}
                            </span>
                          )}
                          {getRateLabel(item) && (
                            <span className="inline-block text-xs text-primary-600 bg-primary-50 border border-primary-100 rounded-md px-1.5 py-0.5">
                              {getRateLabel(item)}
                            </span>
                          )}
                          {!getQtyLabel(item) && !getRateLabel(item) && item.unit && (
                            <span className="inline-block text-xs text-gray-400 bg-gray-50 rounded-md px-1.5 py-0.5">
                              per {item.unit}
                            </span>
                          )}
                          {itemDeliveryCharge > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-1.5 py-0.5">
                              <FiTruck className="w-3 h-3" /> ₹{itemDeliveryCharge.toFixed(2)} delivery
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => { removeItem(item.id); toast.success("Item removed"); }}
                      className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      aria-label="Remove item"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Price + Qty row */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => updateQty(item.id, item.qty - 1)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-primary-50 hover:text-primary-600 transition-colors text-gray-500"
                        aria-label="Decrease"
                      >
                        <FiMinus className="w-3 h-3" />
                      </button>
                      <span className="w-7 text-center text-sm font-semibold text-gray-800">{item.qty}</span>
                      <button
                        onClick={() => updateQty(item.id, item.qty + 1)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-primary-50 hover:text-primary-600 transition-colors text-gray-500"
                        aria-label="Increase"
                      >
                        <FiPlus className="w-3 h-3" />
                      </button>
                    </div>

                    <div className="text-right">
                      <p className="font-bold text-gray-900 text-sm">₹{(item.price * item.qty).toFixed(2)}</p>
                      {item.qty > 1 && (
                        <p className="text-xs text-gray-400">₹{item.price} each</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Continue Shopping */}
          <Link
            to="/products"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-dashed border-gray-200 text-sm text-gray-400 hover:border-primary-300 hover:text-primary-500 hover:bg-primary-50/40 transition-all font-medium"
          >
            <FiShoppingBag className="w-4 h-4" /> Continue Shopping
          </Link>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-gray-100 rounded-2xl p-5 sticky top-24 shadow-sm">

            <h3 className="font-bold text-gray-900 text-base mb-5">Order Summary</h3>

            <div className="space-y-3">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Subtotal ({items.length} item{items.length !== 1 ? "s" : ""})</span>
                <span className="text-gray-700 font-medium">₹{total.toFixed(2)}</span>
              </div>

              {savings > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-600 flex items-center gap-1"><FiTag className="w-3 h-3" /> Savings</span>
                  <span className="text-green-600 font-medium">−₹{savings.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm text-gray-500">
                <span>Delivery Charge{totalKg > 0 ? ` (${totalKg.toFixed(2)} kg)` : ""}</span>
                <span className="text-gray-700 font-medium">₹{deliveryCharge.toFixed(2)}</span>
              </div>

              <div className="flex justify-between text-sm text-gray-500">
                <span>SGST ({sgstPercent}%)</span>
                <span className="text-gray-700 font-medium">₹{sgstAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between text-sm text-gray-500">
                <span>CGST ({cgstPercent}%)</span>
                <span className="text-gray-700 font-medium">₹{cgstAmount.toFixed(2)}</span>
              </div>

              <div className="h-px bg-gray-100 my-1" />

              <div className="flex justify-between items-center">
                <span className="font-bold text-gray-900">Total</span>
                <span className="font-bold text-xl text-primary-600">₹{grandTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleCheckout}
              className="btn-primary w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm"
            >
              Proceed to Checkout <FiArrowRight className="w-4 h-4" />
            </button>

            <p className="text-center text-xs text-gray-400 mt-3">
              Secure & easy checkout • No hidden charges
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}